/**
 * Domain Detection Utilities
 *
 * Shared domain detection logic used across all apps.
 */

export type AppDomain = 'portal' | 'ops' | 'marketing'

/**
 * Detect which domain/app we're serving based on hostname.
 * In development, can be overridden via localStorage for testing.
 */
export function getAppDomain(): AppDomain {
  // Development override - allows testing different domains locally
  if (import.meta.env.DEV) {
    const devOverride = localStorage.getItem('dev_domain') as AppDomain | null
    if (devOverride && ['portal', 'ops', 'marketing'].includes(devOverride)) {
      return devOverride
    }
  }

  const hostname = window.location.hostname

  // Production domain detection
  if (hostname.startsWith('portal.')) {
    return 'portal'
  }
  if (hostname.startsWith('ops.')) {
    return 'ops'
  }

  // Default: marketing site (main domain, www, or localhost)
  return 'marketing'
}

/**
 * Helper to switch domain in development
 * Usage: switchDevDomain('portal') then refresh
 */
export function switchDevDomain(domain: AppDomain): void {
  if (!import.meta.env.DEV) {
    console.warn('[Domain] Domain switching only available in development')
    return
  }
  localStorage.setItem('dev_domain', domain)
  console.log(`[Domain] Dev domain set to: ${domain}. Refresh to apply.`)
  window.location.reload()
}
